/* biome-ignore-all lint/suspicious/noExplicitAny: legacy data structure */
import React, { useState } from "react"
import {
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Button,
  message,
  Alert,
} from "antd"
import { FormatPainterOutlined } from "@ant-design/icons"
import { settingService } from "@/services/system/settingApi"

const { TextArea } = Input

interface CreateSettingModalProps {
  open: boolean
  groups: string[]
  onClose: () => void
  onSuccess: () => void
}

export const CreateSettingModal: React.FC<CreateSettingModalProps> = ({
  open,
  groups,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [jsonError, setJsonError] = useState<string | null>(null)

  const groupOptions = Array.from(
    new Set([...groups, "sets", "system", "home", "banner", "gamification"])
  ).map((g) => ({
    label: g,
    value: g,
  }))

  const handleFormatJson = () => {
    try {
      const current = form.getFieldValue("payload")
      if (!current) return
      const parsed = JSON.parse(current)
      form.setFieldsValue({ payload: JSON.stringify(parsed, null, 2) })
      setJsonError(null)
      message.success("Đã định dạng JSON!")
    } catch (e: any) {
      setJsonError(`Lỗi cú pháp: ${e.message}`)
    }
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      let parsedPayload: any
      try {
        parsedPayload = JSON.parse(values.payload)
      } catch (e: any) {
        setJsonError(`JSON không hợp lệ: ${e.message}`)
        return
      }

      setLoading(true)
      await settingService.create({
        group: values.group.trim(),
        name: values.name.trim(),
        payload: parsedPayload,
        locked: values.locked ? 1 : 0,
      })

      message.success("Tạo cấu hình mới thành công!")
      form.resetFields()
      setJsonError(null)
      onSuccess()
      onClose()
    } catch (e: any) {
      if (e.message) {
        message.error("Tạo cấu hình thất bại: " + e.message)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title="Tạo Cấu hình Hệ thống Mới"
      open={open}
      onCancel={onClose}
      width={640}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={loading}>
          Hủy
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit} loading={loading}>
          Tạo cấu hình
        </Button>,
      ]}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          group: "sets",
          locked: false,
          payload: "[\n  \n]",
        }}
        className="pt-2"
      >
        <Form.Item
          name="group"
          label="Nhóm cấu hình (Group)"
          rules={[{ required: true, message: "Vui lòng chọn hoặc nhập nhóm cấu hình!" }]}
          extra="Ví dụ: sets, system, home, banner,..."
        >
          <Select
            placeholder="Chọn hoặc nhập tên nhóm"
            options={groupOptions}
            showSearch
            allowClear
          />
        </Form.Item>

        <Form.Item
          name="name"
          label="Tên cấu hình (Name)"
          rules={[
            { required: true, message: "Vui lòng nhập tên cấu hình!" },
            {
              pattern: /^[a-zA-Z0-9_-]+$/,
              message: "Tên cấu hình chỉ gồm chữ cái, số, gạch dưới (_) hoặc gạch nối (-)",
            },
          ]}
          extra="Tên định danh cấu hình (Ví dụ: others_learning_ids, featured_ids,...)"
        >
          <Input placeholder="ví dụ: featured_ids" />
        </Form.Item>

        <Form.Item
          name="locked"
          label="Khóa bảo vệ (Locked)"
          valuePropName="checked"
          extra="Khi khóa, cấu hình sẽ không thể bị xóa ngoài danh sách."
        >
          <Switch checkedChildren="Khóa" unCheckedChildren="Mở" />
        </Form.Item>

        <Form.Item
          name="payload"
          label={
            <div className="flex items-center justify-between w-full">
              <span>Dữ liệu JSON (Payload)</span>
              <Button
                type="link"
                size="small"
                icon={<FormatPainterOutlined />}
                onClick={handleFormatJson}
                className="p-0 h-auto"
              >
                Format JSON
              </Button>
            </div>
          }
          rules={[{ required: true, message: "Vui lòng nhập dữ liệu JSON!" }]}
        >
          <TextArea
            rows={8}
            className="font-mono text-xs bg-slate-900 text-green-400 p-3 rounded"
            placeholder="[1, 2, 3] hoặc { ... }"
            onChange={(e) => {
              try {
                JSON.parse(e.target.value)
                setJsonError(null)
              } catch (err: any) {
                setJsonError(`Lỗi cú pháp: ${err.message}`)
              }
            }}
          />
        </Form.Item>

        {jsonError && (
          <Alert message={jsonError} type="error" showIcon className="text-xs mb-3" />
        )}
      </Form>
    </Modal>
  )
}
